// Permissions hook, backed by TanStack Query.
// Fetches the Auth0 access token once per session, decodes the `permissions` claim,
// and mirrors the result to localStorage["permissions"] so the legacy Angular app can
// reuse the same cache during the cutover window.
import { useCallback, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useQuery } from '@tanstack/react-query';
import { jwtDecode } from 'jwt-decode';

const STORAGE_KEY = 'permissions';

interface DecodedToken {
  permissions?: string[];
  [claim: string]: unknown;
}

function readCached(): string[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export interface PermissionsResult {
  permissions: string[];
  claims: DecodedToken | null;
  hasAny: (required: string[] | undefined) => boolean;
  isLoading: boolean;
}

export function usePermissions(): PermissionsResult {
  const { isAuthenticated, getAccessTokenSilently } = useAuth0();

  const query = useQuery({
    queryKey: ['auth', 'permissions'],
    enabled: isAuthenticated,
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false,
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      return jwtDecode<DecodedToken>(token);
    },
  });

  useEffect(() => {
    if (query.data?.permissions) {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify(query.data.permissions),
      );
    }
  }, [query.data]);

  const permissions =
    query.data?.permissions ?? (isAuthenticated ? readCached() : []);

  const hasAny = useCallback(
    (required: string[] | undefined): boolean => {
      if (!required || required.length === 0) return true;
      return required.some((r) => permissions.includes(r));
    },
    [permissions],
  );

  return {
    permissions,
    claims: query.data ?? null,
    hasAny,
    isLoading: isAuthenticated && query.isLoading,
  };
}
